import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// UI
import ModelTable, { IModelTableProps } from '@/ui/Table/ModelTable';
import TableButtonDelete from '@/ui/Table/ActionButtons/TableButtonDelete';
import TableButtonEdit from '@/ui/Table/ActionButtons/TableButtonEdit';
import TableButtonView from '@/ui/Table/ActionButtons/TableButtonView';
import GeneralDialog from '@/ui/Dialog/GeneralDialog';

// Context
import DialogContext from '@/context/DialogContext/DialogContext';

// Types
import { DataResponse, DCRecord } from '@/@types/lib/dataController';
import { ClosingDetails } from '@/@types/components/authFormController';
import TableHeaderButtonShowDeleted from './HeaderButtons/TableHeaderButtonShowDeleted';
import TableButtonDocuments from './ActionButtons/TableButtonDocuments';
import useDocuments from '@/components/useDocuments';
import LoaderContext from '@/context/LoaderContext/LoaderContext';

interface IDCTable extends IModelTableProps {
  title: string;
  allowView?: boolean;
  allowAdd?: boolean;
  allowEdit?: boolean;
  allowDelete?: boolean;
  dialogComponent?: React.FunctionComponent;
  allowShowDeleted?: boolean;
  allowDocumentButton?: boolean;
  foreignKeyValue?: DCRecord;
}

const DCTable = (props: IDCTable) => {
  const { dc, allowView, allowEdit, allowDelete, title, dialogComponent, allowShowDeleted, allowDocumentButton, foreignKeyValue } = props;

  const { t } = useTranslation();

  const { showDialog, showConfirmDialog } = useContext(DialogContext);
  const { toggleLoading } = useContext(LoaderContext);

  const { checkIfHasGid, showDocumentsDialog } = useDocuments();

  const [records, setRecords] = useState<DCRecord[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);

  const filteredRecords = useMemo(() => {
    if (!allowShowDeleted) return records;
    if (showDeleted) return records;
    return records.filter((rec) => !rec.is_deleted);
  }, [records, showDeleted]);

  const deletedHeaderButton = allowShowDeleted ? (
    <TableHeaderButtonShowDeleted showDeleted={showDeleted} onClick={() => setShowDeleted((prev) => !prev)} />
  ) : null;

  const refreshRecords = () => {
    toggleLoading(true);
    dc.GetData()
      .then((resp: DataResponse) => {
        if (resp.success && Array.isArray(resp.data)) {
          setRecords(resp.data);
        }
      })
      .finally(() => {
        toggleLoading(false);
      });
  };

  const handleView = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    if (record?.id) {
      showDialog(dialogComponent ?? GeneralDialog, {
        dc,
        mode: 'view',
        initialRecord: record,
        onClose: handleDialogClose,
        title: `${t(title)} - ${t('common.view')}`,
      });
    }
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    if (record?.id) {
      showDialog(
        dialogComponent ?? GeneralDialog,
        {
          dc,
          mode: 'update',
          form: 'update',
          initialRecord: record,
          onClose: handleDialogClose,
          title: `${t(title)} - ${t('common.update')}`,
        },
        2
      );
    }
  };

  const handleAdd = () => {
    showDialog(
      dialogComponent ?? GeneralDialog,
      {
        dc,
        mode: 'insert',
        form: 'insert',
        onClose: handleDialogClose,
        title: `${t(title)} - ${t('common.insert')}`,
        initialRecord: foreignKeyValue,
      },
      2
    );
  };

  const handleDelete = async (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const record = sel[Object.keys(sel)[0]]; // TODO handle multiple?

    const confirmResult = await showConfirmDialog({
      title: t(title) as string,
      text: `${t('cdialogs.are_you_sure')}`,
      confirmButtonText: t('buttons.confirm') as string,
      cancelButtonText: t('buttons.cancel') as string,
    });

    if (confirmResult.success && record.id) {
      await dc.DeleteRecord(record.id as number);
      refreshRecords();
    }
  };

  const handleDialogClose = (result: ClosingDetails) => {
    switch (result.action) {
      case 'insert':
      case 'update':
      case 'delete':
        if (result.dataChanged) refreshRecords(); // only refresh if something changed
        break;
      case 'cancel': // if user canceled don't refresh
        break;
      default:
        if (result.dataChanged) refreshRecords();
        break;
    }
  };

  const handleDocuments = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];
    const gid = record.gid as string;

    if (gid) {
      showDocumentsDialog(gid, handleDialogClose);
    }
  };

  const isDocumentsButtonEnabled: (selection: { [key: string]: DCRecord }) => boolean = (sel) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];

    if (allowShowDeleted) {
      return !!record.gid && !record.is_deleted;
    }

    return !!record.gid;
  };

  useEffect(() => {
    refreshRecords(); // fetch records on first render
  }, []);

  const hasGid = checkIfHasGid(records);

  return (
    
    <ModelTable handleAdd={handleAdd} records={filteredRecords} customHeaderControl={deletedHeaderButton} {...props}>
      {allowDocumentButton && hasGid ? <TableButtonDocuments isEnabledRecord={isDocumentsButtonEnabled} onClick={handleDocuments} /> : null}
      {allowDelete ? <TableButtonDelete variant="outlined" onClick={handleDelete} /> : null}
      {allowEdit ? <TableButtonEdit variant="outlined" onClick={handleEdit} /> : null}
      {allowView ? <TableButtonView variant="contained" onClick={handleView} /> : null}
    </ModelTable>
  );
};

export default DCTable;
